import React from 'react';

import { Box, Typography } from '@mui/material';

import { SuggestedActionType } from '../types';

import { formatCurrency, formatSignedCurrency, formatSignedPercent, getChangeColor } from './toursPricingUtils';

type SuggestedPricingRow = {
  suggested_action_type: SuggestedActionType;
  original_tour_price: number;
  current_tour_price?: number | null;
  suggested_price_change_percent: number;
  suggested_price_change_amount?: number | null;
  after_price: number;
};

type AppliedPricingRow = {
  applied_action_type: SuggestedActionType;
  original_tour_price: number;
  current_tour_price?: number | null;
  suggested_price_change_percent: number;
  applied_price_change_percent: number;
  applied_after_price: number;
};

type ToursPricingFormatterProps =
  | { row: SuggestedPricingRow; mode?: 'suggested' }
  | { row: AppliedPricingRow; mode: 'applied' };

function getNetChangeColor(value: number | null): string {
  if (value == null || value === 0) {
    return 'text.primary';
  }
  return value > 0 ? 'success.main' : 'error.main';
}

function getCurrentLivePrice(row: SuggestedPricingRow | AppliedPricingRow): number | null {
  if (row.current_tour_price != null) {
    return row.current_tour_price;
  }
  return row.original_tour_price;
}

function getPricingSnapshot(row: SuggestedPricingRow | AppliedPricingRow, mode: 'suggested' | 'applied') {
  if (mode === 'applied') {
    const appliedRow = row as AppliedPricingRow;
    const livePrice = appliedRow.applied_after_price;
    const changeAmount = livePrice - appliedRow.original_tour_price;
    return {
      actionType: appliedRow.applied_action_type,
      suggestedChangePercent: appliedRow.suggested_price_change_percent,
      changePercent: appliedRow.applied_price_change_percent,
      changeAmount,
      livePrice,
      labelPrefix: 'Applied',
      wasOverridden: appliedRow.applied_price_change_percent !== appliedRow.suggested_price_change_percent,
    };
  }

  const suggestedRow = row as SuggestedPricingRow;
  return {
    actionType: suggestedRow.suggested_action_type,
    suggestedChangePercent: null,
    changePercent: suggestedRow.suggested_price_change_percent,
    changeAmount: suggestedRow.suggested_price_change_amount ?? null,
    livePrice: suggestedRow.after_price,
    labelPrefix: 'Proposed',
    wasOverridden: null,
  };
}

export default function ToursPricingFormatter({ row, mode = 'suggested' }: ToursPricingFormatterProps) {
  const pricingSnapshot = getPricingSnapshot(row, mode);
  const changeColor = getChangeColor(pricingSnapshot.actionType);
  const currentLivePrice = getCurrentLivePrice(row);
  const netVsCurrentAmount =
    currentLivePrice == null || pricingSnapshot.livePrice == null ? null : pricingSnapshot.livePrice - currentLivePrice;
  const netVsCurrentPercent =
    currentLivePrice != null && currentLivePrice !== 0 && netVsCurrentAmount != null
      ? netVsCurrentAmount / currentLivePrice
      : null;

  return (
    <Box sx={{ alignSelf: 'flex-start', py: 1 }}>
      <Typography variant="body2">
        <Typography component="span" fontWeight="bold" variant="body2">
          {pricingSnapshot.labelPrefix} override:
        </Typography>{' '}
        <Typography component="span" variant="body2" color={changeColor} fontWeight="medium">
          {formatSignedPercent(pricingSnapshot.changePercent)}
        </Typography>
      </Typography>
      {pricingSnapshot.suggestedChangePercent != null && (
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold" variant="body2">
            Suggested override:
          </Typography>{' '}
          <Typography component="span" variant="body2" fontWeight="medium">
            {formatSignedPercent(pricingSnapshot.suggestedChangePercent)}
          </Typography>
        </Typography>
      )}
      {pricingSnapshot.wasOverridden != null && (
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold" variant="body2">
            Was overridden:
          </Typography>{' '}
          <Typography component="span" variant="body2" fontWeight="medium">
            {pricingSnapshot.wasOverridden ? 'Yes' : 'No'}
          </Typography>
        </Typography>
      )}
      {pricingSnapshot.changeAmount != null && (
        <Typography variant="body2">
          <Typography component="span" fontWeight="bold" variant="body2">
            {pricingSnapshot.labelPrefix} vs base:
          </Typography>{' '}
          <Typography component="span" variant="body2" color={changeColor} fontWeight="medium">
            {formatSignedCurrency(pricingSnapshot.changeAmount)}
          </Typography>
        </Typography>
      )}
      <Typography variant="body2">
        <Typography component="span" fontWeight="bold" variant="body2">
          {pricingSnapshot.labelPrefix} live:
        </Typography>{' '}
        {formatCurrency(pricingSnapshot.livePrice)}
      </Typography>
      <Typography variant="body2">
        <Typography component="span" fontWeight="bold" variant="body2">
          Net vs current:
        </Typography>{' '}
        <Typography component="span" variant="body2" color={getNetChangeColor(netVsCurrentAmount)} fontWeight="medium">
          {formatSignedCurrency(netVsCurrentAmount)}
        </Typography>
        {netVsCurrentPercent != null && (
          <Typography
            component="span"
            variant="body2"
            color={getNetChangeColor(netVsCurrentAmount)}
            fontWeight="medium"
          >
            {' '}
            ({formatSignedPercent(netVsCurrentPercent)})
          </Typography>
        )}
      </Typography>
    </Box>
  );
}
